import type { ChangeEvent } from "react"
import EntryFormField, {
  FIELD_CONTROL_CLASS,
} from "@/components/entry/EntryFormField"
import { toTodayInputDate } from "@/domain/common/utils/DateUtils"
import { GENDER_OPTIONS } from "@/domain/entry/model/Entry"
import { isPlayerRecordCollectedByValue } from "@/domain/fight/model/Fight"
import type {
  EntryPlayerErrors,
  EntryPlayerInput,
  EntryPlayerTextField,
  SelectOption,
} from "@/domain/entry/model/Entry"
import type { WeightClassChoicesState } from "@/domain/fight/model/Fight"

interface EntryPlayerFieldsProps {
  player: EntryPlayerInput
  index: number
  errors: EntryPlayerErrors
  sportOptions: SelectOption[]
  /** 그 선수가 고를 수 있는 체급 — 종목 · 생년월일 · 성별로 서버가 정한다 */
  weightClassChoices: WeightClassChoicesState
  canDelete: boolean
  onChange: (field: EntryPlayerTextField, value: string) => void
  onChangeWeightClass: (optionKey: string) => void
  onDelete: () => void
}

export default function EntryPlayerFields({
  player,
  index,
  errors,
  sportOptions,
  weightClassChoices,
  canDelete,
  onChange,
  onChangeWeightClass,
  onDelete,
}: Readonly<EntryPlayerFieldsProps>) {
  const idPrefix = `player-${player.key}`
  /** 생년월일은 미래를 고를 수 없다 — 달력에서 오늘 이후가 잠긴다 */
  const maxBirthDate = toTodayInputDate()
  /** 닭싸움은 이름 · 생년월일 · 성별 · 종목 넷만 받는다 — 체급 · 체중 · 경력 · 출전 횟수 칸이 없다 */
  const isRecordCollected = isPlayerRecordCollectedByValue(player.sport)
  const hasWeightClassChoice = weightClassChoices.options.length > 0
  /** 체급표가 없어 접수 후 운영자가 확정하는 상태 — 이때만 체급이 필수가 아니다 */
  const isWeightClassDeferred =
    weightClassChoices.isAsked && !hasWeightClassChoice

  function onChangeSport(event: ChangeEvent<HTMLSelectElement>) {
    onChange("sport", event.target.value)
  }

  function onChangeWeightClassOption(event: ChangeEvent<HTMLSelectElement>) {
    onChangeWeightClass(event.target.value)
  }

  function onChangeBirthDate(event: ChangeEvent<HTMLInputElement>) {
    onChange("birthDate", event.target.value)
  }

  function onChangeName(event: ChangeEvent<HTMLInputElement>) {
    onChange("name", event.target.value)
  }

  function onChangeGender(event: ChangeEvent<HTMLSelectElement>) {
    onChange("gender", event.target.value)
  }

  function onChangeWeightKg(event: ChangeEvent<HTMLInputElement>) {
    onChange("weightKg", event.target.value)
  }

  function onChangeCareerYears(event: ChangeEvent<HTMLInputElement>) {
    onChange("careerYears", event.target.value)
  }

  function onChangeCompetitionCount(event: ChangeEvent<HTMLInputElement>) {
    onChange("competitionCount", event.target.value)
  }

  function onClickDelete() {
    onDelete()
  }

  const deleteButtonClass =
    "focus-ring app-btn app-btn-sm app-btn-quiet hover:bg-app-red-100 hover:text-app-red-300"

  return (
    <article className="border border-app-secondary-500 bg-app-secondary-400 p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between xl:hidden">
        <p className="app-txt-body2 font-black">선수 {index + 1}</p>
        {canDelete && (
          <button
            type="button"
            onClick={onClickDelete}
            className={deleteButtonClass}
          >
            선수 {index + 1} 삭제
          </button>
        )}
      </div>

      <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[minmax(0,1fr)_minmax(140px,0.95fr)_minmax(112px,0.85fr)_minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,0.75fr)_minmax(0,0.75fr)_minmax(0,0.75fr)_auto] xl:items-start xl:gap-x-5">
        <EntryFormField
          controlId={`${idPrefix}-name`}
          label="이름"
          isRequired
          error={errors.name}
        >
          <input
            id={`${idPrefix}-name`}
            type="text"
            value={player.name}
            maxLength={20}
            onChange={onChangeName}
            placeholder="선수 이름"
            aria-invalid={Boolean(errors.name)}
            className={FIELD_CONTROL_CLASS}
          />
        </EntryFormField>

        <EntryFormField
          controlId={`${idPrefix}-birth-date`}
          label="생년월일"
          isRequired
          error={errors.birthDate}
        >
          <input
            id={`${idPrefix}-birth-date`}
            type="date"
            value={player.birthDate}
            onChange={onChangeBirthDate}
            max={maxBirthDate}
            aria-invalid={Boolean(errors.birthDate)}
            className={FIELD_CONTROL_CLASS}
          />
        </EntryFormField>

        <EntryFormField
          controlId={`${idPrefix}-gender`}
          label="성별"
          isRequired
          error={errors.gender}
        >
          <select
            id={`${idPrefix}-gender`}
            value={player.gender}
            onChange={onChangeGender}
            aria-invalid={Boolean(errors.gender)}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="">성별 선택</option>
            {GENDER_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </EntryFormField>

        <EntryFormField
          controlId={`${idPrefix}-sport`}
          label="종목"
          isRequired
          error={errors.sport}
        >
          <select
            id={`${idPrefix}-sport`}
            value={player.sport}
            onChange={onChangeSport}
            aria-invalid={Boolean(errors.sport)}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="">종목 선택</option>
            {sportOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
        </EntryFormField>

        {isRecordCollected && (
          <>
            <EntryFormField
              controlId={`${idPrefix}-weight-class`}
              label="체급"
              isRequired={hasWeightClassChoice}
              optionalHint={isWeightClassDeferred ? "운영자 확정" : ""}
              error={errors.weightClass}
            >
              <select
                id={`${idPrefix}-weight-class`}
                value={player.weightClass?.key ?? ""}
                onChange={onChangeWeightClassOption}
                disabled={!hasWeightClassChoice}
                aria-invalid={Boolean(errors.weightClass)}
                aria-describedby={
                  weightClassChoices.notice
                    ? `${idPrefix}-weight-class-notice`
                    : undefined
                }
                className={FIELD_CONTROL_CLASS}
              >
                <option value="">
                  {weightClassChoices.isLoading ? "불러오는 중…" : "체급 선택"}
                </option>
                {weightClassChoices.options.map((option) => (
                  <option
                    key={option.key}
                    value={option.key}
                  >
                    {option.label}
                  </option>
                ))}
              </select>

              {weightClassChoices.notice && (
                <p
                  id={`${idPrefix}-weight-class-notice`}
                  className="app-field-help"
                >
                  {weightClassChoices.notice}
                </p>
              )}
            </EntryFormField>

            <EntryFormField
              controlId={`${idPrefix}-weight-kg`}
              label="체중 (kg)"
              isRequired
              error={errors.weightKg}
            >
              <input
                id={`${idPrefix}-weight-kg`}
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={player.weightKg}
                onChange={onChangeWeightKg}
                placeholder="59.8"
                aria-invalid={Boolean(errors.weightKg)}
                className={FIELD_CONTROL_CLASS}
              />
            </EntryFormField>

            <EntryFormField
              controlId={`${idPrefix}-career-years`}
              label="경력 (년)"
              isRequired
              error={errors.careerYears}
            >
              <input
                id={`${idPrefix}-career-years`}
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={player.careerYears}
                onChange={onChangeCareerYears}
                placeholder="2.5"
                aria-invalid={Boolean(errors.careerYears)}
                className={FIELD_CONTROL_CLASS}
              />
            </EntryFormField>

            <EntryFormField
              controlId={`${idPrefix}-competition-count`}
              label="출전 횟수"
              isRequired
              error={errors.competitionCount}
            >
              <input
                id={`${idPrefix}-competition-count`}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={player.competitionCount}
                onChange={onChangeCompetitionCount}
                placeholder="0"
                aria-invalid={Boolean(errors.competitionCount)}
                className={FIELD_CONTROL_CLASS}
              />
            </EntryFormField>
          </>
        )}

        {canDelete && (
          // 삭제 버튼은 넓은 화면에서 늘 마지막 칸이다 — 칸이 빠져도 자리를 지킨다
          <div className="hidden xl:col-start-9 xl:block">
            <span
              aria-hidden="true"
              className="app-label mb-1.5 block text-transparent"
            >
              삭제
            </span>
            <button
              type="button"
              onClick={onClickDelete}
              className={deleteButtonClass}
            >
              <span aria-hidden="true">삭제</span>
              <span className="sr-only">선수 {index + 1} 삭제</span>
            </button>
          </div>
        )}
      </div>
    </article>
  )
}
